// assayDraft vuex module – holds transient state while user steps through assay wizard.
// once finalized, state is pushed to backend via dedicated service.
// ensure structure mirrors spreadsheet sections so mapping is trivial.

import _ from 'lodash'
import request from '@/util/request'
import { calculateAssayLossIfNeeded } from '@/util/refinery/barCalculations'
import { safeExtractBarNumber } from '@/util/refinery/barNumberUtils'
import {
  DEFAULT_METAL_SYMBOL,
  VALID_METADATA_FIELDS,
  ERROR_MESSAGES,
  DEFAULT_MARGIN_OF_ERROR,
  DEFAULT_ASSAY_TAT_DAYS,
  DEFAULT_SECONDARY_HAIRCUT_FACTOR,
  DEFAULT_WEIGHT_PRECISION,
  DEFAULT_PURITY_PRECISION,
  DEFAULT_PGM_HAIRCUT_FACTOR,
  METAL_SYMBOL
} from '@/util/refinery/constants'
import { calculateCrossBarAverages, calculateFireCrossBarAverages, canUseWeightedAveraging } from '@/util/refinery/crossBarCalculations'
import { parseLegacyBarsIntoPins, projectPinsToLegacyBars } from '@/util/refinery/legacyBarPinFacade'
import {
  buildDistinctPayload,
  buildPinSerialNumber,
  findCustomerPinForBar,
  findHousePinForBar,
  findHousePinWeightForBar,
  PIN_KINDS,
  pinBelongsToBar,
  pinsForBar,
  setCustomerPinWeight,
  setHousePinWeight,
  stripEmbeddedPinFields,
  upsertHousePin
} from '@/util/refinery/refiningLotPinState'
import { normalizeBarWeights, normalizePinWeights, normalizeWeightArray } from '@/util/refinery/weightFieldNormalizer'
import { convertToRichWeight, extractDisplayWeight, getContextUnit } from '@/util/refinery/weightUtils'
import { registerAssayPhoto, confirmAssayPhoto, deleteAssayPhoto, setAssayPhotoVisibility } from '@/util/refinery/assayPhotosApi'
import { uploadAssayPhotoToS3 } from '@/util/refinery/uploadAssayPhotoToS3'
import Vue from 'vue'

const DISTINCT_PINS_FLAG = 'REFINERY_LOT_PINS_DISTINCT'

const initialState = () => ({
  lotId: null,
  lotAssayId: null,
  assayResultIds: [],
  lotCode: null, // human-friendly code like RL24124
  lotDetails: null, // full lot data including contract info
  // meta section
  metadata: {
    assayDueDate: null, // date string
    advanceRequested: null, // 'Yes' | 'No'
    customerPinRequired: 'No', // 'Yes' | 'No' (default to No)
    returnOrHoldPin: null, // 'Yes' | 'No'
    metaConfirmed: false, // set true when Melt Instructions validated/confirmed
    meltCompletedAt: null, // iso datetime string
    furnace: null,
    meltTechnicianId: null,
    barGrossWeight: null,
    notes: '',
    fireAssayBatch: null,
    metalSymbol: null,
    marginOfError: DEFAULT_MARGIN_OF_ERROR, // Margin of error for fire assay measurements
    pgmHaircutFactor: null, // PGM haircut factor
    secondaryHaircutFactor: null, // Secondary metal haircut factor
  },
  // array of bars – manifest step
  bars: [], // current manifest bars
  allBars: [], // other bars fetched for lot
  pins: [], // draft pins for bars in the current assay manifest
  allPins: [], // lot-wide pin inventory (other-bars picker; not editable here)
  // calculation overrides (per-lot)
  calcOverrides: {},
  // workflow flags
  metaConfirmed: false,
  // lab results
  xrfResults: {}, // keyed by barNumber → per-metal results
  fireResults: {},
  // per-assay notes keyed by barNumber → { xrf, cup }
  resultNotes: {},
  manifestConfirmed: false,
  processedAt: null, // ISO timestamp when manifest was confirmed
  startingGrossWeight: null, // initial pre-melt weight for assay

  // NEW: additional fields from backend (read-only in UI)
  processWeight: null,         // bars + house pins (from backend)
  pinWeight: null,             // total house pins (from backend)
  finalPinWeight: null,        // pins after fire assay (from backend)
  meltLoss: null,              // received - process (from backend)
  grossWeight: null,           // process - assayLoss (from backend)
  netWeight: null,             // gross - customerPins (from backend)
  status: 'draft', // 'draft' | 'submitting' | 'submitted'
})

const state = initialState()

const getters = {
  assayDraft: (state) => state,
  hasFullAssayView: (state) => Boolean(state.lotDetails?.can?.fullAssayView),
  pinsForBar: (state) => bar => pinsForBar(bar, state.pins),
  findHousePinForBar: (state) => bar => findHousePinForBar(bar, state.pins),
  findCustomerPinForBar: (state) => bar => findCustomerPinForBar(bar, state.pins),
  barsNotInManifest: (state)=>{
    const manifestNums = state.bars.map(b=> parseInt(b.barNumber))
    return state.allBars.filter(b=> !manifestNums.includes(parseInt(b.barNumber)))
  },
  
  // xrf cross-bar averaging getters
  useWeightedXrfAveraging: (state) => 
    state.calcOverrides?.useWeightedXrfAveraging ?? true,
  
  // Storage precision: decimal places in raw storage (0.9287 = 4 decimals)
  purityStoragePrecision: (state, getters, rootState, rootGetters) => 
    state.calcOverrides?.purityPrecision ?? rootGetters.refinerySetting('purityPrecision', DEFAULT_PURITY_PRECISION),
  
  // Display precision: decimal places user sees (92.87% = 2 decimals)
  // Purity stored as decimal (0-1) but displayed as percentage (0-100)
  // So subtract 2: 4 decimals on 0.9287 = 2 decimals on 92.87%
  purityPrecision: (state, getters) => 
    Math.max(0, getters.purityStoragePrecision - 2),
  
  // Fine troy ounce precision: decimal places for weight calculations
  fineOztPrecision: (state, getters, rootState, rootGetters) => 
    state.calcOverrides?.fineOztPrecision ?? rootGetters.refinerySetting('fineOztPrecision', DEFAULT_WEIGHT_PRECISION),
  
  pgmHaircutFactor: (state, getters, rootState, rootGetters) =>
    state.metadata?.pgmHaircutFactor ??
    rootGetters.refinerySetting('pgmHaircutFactor', DEFAULT_PGM_HAIRCUT_FACTOR),
  
  crossBarAverages: (state, getters) => {
    const primaryMetal = state.metadata.metalSymbol || METAL_SYMBOL.GOLD
    return calculateCrossBarAverages(
      state.xrfResults, 
      state.bars, 
      primaryMetal, 
      getters.useWeightedXrfAveraging,
      getters.purityStoragePrecision,
      getters.pgmHaircutFactor
    )
  },
  
  primaryMetalAverage: (state, getters) => getters.crossBarAverages.primary,
  
  canUseWeightedXrfAveraging: (state) => canUseWeightedAveraging(state.bars),
  
  xrfAveragingMethodDisplay: (state, getters) => {
    if (!getters.useWeightedXrfAveraging || !getters.canUseWeightedXrfAveraging || state.bars.length <= 1) {
      return ''
    }
    return 'weighted by bar size'
  },
  
  // fire assay cross-bar averaging getters
  useWeightedFireAveraging: (state, getters, rootState, rootGetters) => 
    state.calcOverrides?.useWeightedFireAveraging ?? 
    rootGetters.refinerySetting('useWeightedFireAveraging', true),
  
  secondaryHaircutFactor: (state, getters, rootState, rootGetters) =>
    state.metadata?.secondaryHaircutFactor ??
    rootGetters.refinerySetting('secondaryHaircutFactor', DEFAULT_SECONDARY_HAIRCUT_FACTOR),
  
  canUseWeightedFireAveraging: (state) => {
    // Check if all bars with fire results have weights
    const barsWithFire = Object.keys(state.fireResults || {})
    if (!barsWithFire.length) return false
    
    return barsWithFire.every(barNum => {
      const bar = state.bars.find(b => String(b.barNumber) === String(barNum))
      const weight = extractDisplayWeight(bar?.grossWeight, 'bar')
      return bar && weight && weight > 0
    })
  },
  
  fireAveragingMethodDisplay: (state, getters) => {
    if (!getters.useWeightedFireAveraging || !getters.canUseWeightedFireAveraging || state.bars.length <= 1) {
      return ''
    }
    return 'weighted by bar size'
  },
  
  fireAssayAverages: (state, getters) => {
    const primaryMetal = state.metadata.metalSymbol || METAL_SYMBOL.GOLD
    // Use actual margin of error from metadata to match backend calculation
    // Returns margin-adjusted values (what backend uses) + raw values via _raw suffix
    const marginOfError = state.metadata?.marginOfError || DEFAULT_MARGIN_OF_ERROR

    // Always derive Ag for gold assays: normalize doré correction flag to true
    // for any bar that has a doré control sample. This avoids relying on UI
    // toggles and ensures secondary silver is derived via doré differential.
    const ensureDoreOnForAllBars = (fireResults) => {
      const normalized = {}
      Object.entries(fireResults || {}).forEach(([barNum, barResults]) => {
        if (!barResults || typeof barResults !== 'object') {
          normalized[barNum] = barResults
          return
        }
        const control = barResults.control
        const hasDore = control && (control.gross || control.net)
        if (primaryMetal === METAL_SYMBOL.GOLD && hasDore) {
          normalized[barNum] = {
            ...barResults,
            control: { ...(control || {}), useDoreCorrection: true }
          }
        } else {
          normalized[barNum] = barResults
        }
      })
      return normalized
    }

    const fireResultsForCalc = primaryMetal === METAL_SYMBOL.GOLD
      ? ensureDoreOnForAllBars(state.fireResults)
      : state.fireResults

    return calculateFireCrossBarAverages(
      fireResultsForCalc,
      state.bars,
      primaryMetal,
      getters.useWeightedFireAveraging,
      getters.purityStoragePrecision,
      getters.secondaryHaircutFactor,
      marginOfError
    )
  },
  
  combinedAssayResults: (state, getters) => {
    const xrf = getters.crossBarAverages
    const fire = getters.fireAssayAverages
    
    // Fire assay takes precedence for AU/AG (ASTM E1335 compliance)
    const combined = { ...xrf }
    
    // Override with fire values
    if (!_.isNil(fire.AU)) combined.AU = fire.AU
    
    // Always derive Ag when available from fire assay calculations
    if (!_.isNil(fire.AG)) {
      combined.AG = fire.AG
    }
    
    // Merge availableMetals arrays - union of both XRF and Fire
    const xrfMetals = new Set(xrf.availableMetals || [])
    const fireMetals = fire.availableMetals || []
    fireMetals.forEach(metal => xrfMetals.add(metal))
    combined.availableMetals = Array.from(xrfMetals)
    
    // Recalculate primary after blending
    combined.primary = combined[combined.primaryMetal] || null
    
    return combined
  },

  currentAssayPhotos: (state) => {
    if (!state.lotAssayId) return []
    const assay = state.lotDetails?.assays?.find(a => a.id === state.lotAssayId)
    return assay?.photos || []
  },
}

const mutations = {
  RESET_STATE(state) {
    Object.assign(state, initialState())
  },
  SET_LOT_ID(state, lotId) {
    state.lotId = lotId
  },
  SET_LOT_ASSAY_ID(state, id){
    state.lotAssayId = id
  },
  SET_ASSAY_RESULT_IDS(state, ids){
    state.assayResultIds = ids
  },
  SET_LOT_CODE(state, code) {
    state.lotCode = code
  },
  SET_LOT_DETAILS(state, details) {
    state.lotDetails = details
  },
  ADD_PHOTO_TO_ASSAY(state, { lotAssayId, photo }) {
    if (!state.lotDetails?.assays) return
    const assay = state.lotDetails.assays.find(a => a.id === lotAssayId)
    if (!assay) return
    if (!assay.photos) {
      Vue.set(assay, 'photos', [photo]) 
    } else {
      assay.photos.push(photo)
    }
  },
  REMOVE_PHOTO_FROM_ASSAY(state, { lotAssayId, photoId }) {
    if (!state.lotDetails?.assays) return
    const assay = state.lotDetails.assays.find(a => a.id === lotAssayId)
    if (!assay) return
    if (!assay.photos) return
    assay.photos = assay.photos.filter(p => p.id !== photoId)
  },
  UPDATE_PHOTO_IN_ASSAY(state, { lotAssayId, photo }) {
    if (!state.lotDetails?.assays) return
    const assay = state.lotDetails.assays.find(a => a.id === lotAssayId)
    if (!assay || !assay.photos) return
    const index = assay.photos.findIndex(p => p.id === photo.id)
    if (index === -1) return
    Vue.set(assay.photos, index, photo)
  },
  SET_METADATA_FIELD(state, { key, value }) {
    if (VALID_METADATA_FIELDS.includes(key) || key in state.metadata) {
      state.metadata[key] = value
    }
  },
  SET_BARS(state, bars) {
    state.bars = (bars || []).map(stripEmbeddedPinFields)
  },
  SET_ALL_BARS(state,bars){
    state.allBars = (bars || []).map(stripEmbeddedPinFields)
  },
  SET_PINS(state, pins) {
    state.pins = Array.isArray(pins) ? pins : []
  },
  SET_ALL_PINS(state, pins) {
    state.allPins = Array.isArray(pins) ? pins : []
  },
  ADOPT_PINS_FOR_BAR(state, bar) {
    const incoming = state.allPins.filter(pin => pinBelongsToBar(pin, bar))
    if (!incoming.length) return
    state.pins = [
      ...state.pins.filter(pin => !pinBelongsToBar(pin, bar)),
      ...incoming.map(pin => ({ ...pin }))
    ]
  },
  REMOVE_BAR_PINS(state, bar) {
    state.pins = state.pins.filter(pin => !pinBelongsToBar(pin, bar))
  },
  RENUMBER_BAR_PINS(state, renumberingMap) {
    state.pins = state.pins.map(pin => {
      const entry = Object.entries(renumberingMap || {})
        .find(([, bar]) => pinBelongsToBar(pin, bar))
      if (!entry) return pin
      const [newBarNumber, bar] = entry
      const renumberedBarSerial = bar.serialNumber
        ? String(bar.serialNumber).replace(/-\d+$/, `-${newBarNumber}`)
        : null
      return {
        ...pin,
        barNumber: parseInt(newBarNumber),
        ...(renumberedBarSerial ? { serialNumber: buildPinSerialNumber(renumberedBarSerial, pin.kind) } : {})
      }
    })
  },
  SET_XRF_RESULTS(state, payload) {
    state.xrfResults = payload
  },
  SET_STATUS(state, status) {
    state.status = status
  },
  SET_FIRE_RESULTS(state, payload) {
    state.fireResults = payload
    // Automatically calculate and update house-pin assay loss for bars with fire results
    if (payload && state.bars) {
      const isPrimarySilver = state.metadata?.metalSymbol === METAL_SYMBOL.SILVER
      state.bars.forEach(bar => {
        const pin = findHousePinForBar(bar, state.pins)
        if (!pin) return
        const calculatedAssayLoss = calculateAssayLossIfNeeded(
          { barNumber: bar.barNumber, assayLoss: pin.assayLoss },
          payload,
          isPrimarySilver
        )
        if (calculatedAssayLoss) {
          state.pins = upsertHousePin(
            state.pins,
            bar,
            { assayLoss: calculatedAssayLoss }
          )
        }
      })
    }
  },
  SET_RESULT_NOTE(state, { barNumber, assayType, note }) {
    const bn = safeExtractBarNumber(barNumber) || String(barNumber);
    if (!state.resultNotes[bn]) state.resultNotes[bn] = {}
    state.resultNotes[bn] = { ...state.resultNotes[bn], [assayType]: note }
  },
  SET_MANIFEST_CONFIRMED(state, confirmed) {
    state.manifestConfirmed = confirmed;
    // Only set processedAt once (first time manifest is confirmed)
    if (confirmed && !state.processedAt) {
      state.processedAt = new Date().toISOString();
    }
  },
  SET_META_CONFIRMED(state, confirmed) {
    state.metaConfirmed = !!confirmed
  },
  SET_STARTING_GROSS_WEIGHT(state, weight) {
    state.startingGrossWeight = weight
  },
  RESET_RESULTS(state) {
    state.xrfResults = {}
    state.fireResults = {}
    state.resultNotes = {}
    state.metadata.fireAssayBatch = null
    state.metadata.metalSymbol = null
    state.metadata.metaConfirmed = false
  },
  SET_CALC_OVERRIDE(state,{key,value}){
    state.calcOverrides = {...state.calcOverrides, [key]: value}
  },
  SET_BAR_ASSAY_LOSS(state, { barNumber, assayLoss }) {
    const bar = state.bars.find(b => b.barNumber === barNumber)
    if (!bar) return
    state.pins = upsertHousePin(state.pins, bar, {
      assayLoss: extractDisplayWeight(assayLoss, 'troyOunces')
    })
  },
  SET_BAR_HOUSE_PIN_WEIGHT(state, { barNumber, grossWeight }) {
    const bar = state.bars.find(b => b.barNumber === barNumber)
    if (!bar) return
    state.pins = setHousePinWeight(
      state.pins,
      bar,
      extractDisplayWeight(grossWeight, 'troyOunces')
    )
  },
  SET_BAR_CUSTOMER_PIN_WEIGHT(state, { barNumber, grossWeight }) {
    const bar = state.bars.find(b => b.barNumber === barNumber)
    if (!bar) return
    state.pins = setCustomerPinWeight(
      state.pins,
      bar,
      extractDisplayWeight(grossWeight, 'troyOunces')
    )
  },
}

/**
 * Sanitizes bar data for backend validation
 * Converts empty strings to null and uses rich weight format
 * Includes existing bars that have been modified (e.g., melt details)
 * @param {Array} bars - Array of bar objects
 * @returns {Array} - Bars shaped for the wire schema
 */
function sanitizeBarsForBackend(bars) {
  // Normalize various date input formats (e.g. HTML datetime-local) to ISO for backend
  const toIsoIfDateLike = (value) => {
    if (value === undefined || value === null || value === '') return null
    const d = new Date(value)
    return isNaN(d) ? null : d.toISOString()
  }
  return (bars || [])
    .filter(b => {
      // Always include new bars (no ID)
      if (!b.id) return true
      // Include existing bars when any editable field is present (e.g., date/furnace/technician/weights/notes/assayLoss)
      return [
        b.meltCompletedAt,
        b.furnaceId,
        b.meltTechnicianId,
        b.grossWeight,
        b.notes,
      ].some(v => v !== undefined)
    })
    .map(b => {
      const obj = {
        barNumber: parseInt(b.barNumber || 0) || 0,
        grossWeight: convertToRichWeight(b.grossWeight, getContextUnit('bar')),
        furnace: b.furnaceId || null,
        technician: b.meltTechnicianId || null,
        notes: b.notes || null,
        // Always pass meltCompletedAt so edits on existing bars persist (backend will no-op if unchanged)
        // Convert to ISO to ensure consistent server parsing
        meltCompletedAt: (b.meltCompletedAt !== undefined ? toIsoIfDateLike(b.meltCompletedAt) : null),
      }
      return _.omitBy(obj, _.isNil)
    })
}

/**
 * Sanitizes pin rows for backend validation
 * Drops orphaned and weightless pins, and auto-calculates house-pin assay loss
 * from fire results when it has not been entered by hand
 * @param {Array} bars - Array of bar objects the pins hang off of
 * @param {Array} pins - Array of canonical pin rows
 * @param {object} fireResults - Fire results keyed by barNumber
 * @param {boolean} isPrimarySilver - Whether primary metal is silver
 * @returns {Array} - Pins shaped for the wire schema
 */
function sanitizePinsForBackend(bars, pins, fireResults, isPrimarySilver) {
  return (pins || [])
    .filter(pin => bars.some(bar => pinBelongsToBar(pin, bar)))
    .map(pin => {
      const bar = bars.find(candidate => pinBelongsToBar(pin, candidate))
      const assayLoss = pin.kind === PIN_KINDS.HOUSE
        ? calculateAssayLossIfNeeded(
          { barNumber: bar.barNumber, assayLoss: pin.assayLoss },
          fireResults,
          isPrimarySilver
        )
        : null
      return _.omitBy({
        id: pin.id,
        refiningLotBarId: pin.refiningLotBarId,
        barNumber: pin.barNumber ?? bar.barNumber,
        serialNumber: pin.serialNumber,
        kind: pin.kind,
        grossWeight: convertToRichWeight(pin.grossWeight, getContextUnit('pin')),
        assayLoss: convertToRichWeight(assayLoss, getContextUnit('pin'))
      }, _.isNil)
    })
    .filter(pin => pin.grossWeight)
}

const distinctPinsEnabled = rootGetters => {
  const configured = typeof rootGetters.getConfig === 'function'
    ? rootGetters.getConfig(DISTINCT_PINS_FLAG)
    : false
  if (_.isPlainObject(configured)) {
    if (configured.enabled === true || configured.enabled === 'true') return true
    const companyId = rootGetters.getSelectedCompany?.id
    return Boolean(companyId && (configured.companyIds || []).map(String).includes(String(companyId)))
  }
  return configured === true || configured === 'true'
}

const wireBarContract = (bars, pins, useDistinctPins) => {
  if (useDistinctPins) return buildDistinctPayload(bars, pins)
  return { bars: projectPinsToLegacyBars(bars, pins) }
}

const parseBarContract = (data, useDistinctPins) => {
  if (useDistinctPins) {
    return buildDistinctPayload(data?.bars || [], data?.pins || [])
  }
  return parseLegacyBarsIntoPins(Array.isArray(data) ? data : data?.bars || [])
}

/**
 * Calculates total weight from all bars being assayed (including existing bars with weight data)
 */
function calculateTotalAssayWeight(allBars, pins) {
  return allBars.reduce((sum, b) => {
    const gross = extractDisplayWeight(b.grossWeight, 'troyOunces') || 0
    const pin = extractDisplayWeight(findHousePinWeightForBar(b, pins), 'troyOunces') || 0
    return sum + gross + pin
  }, 0)
}

/**
 * Processes result notes into backend format
 */
function processResultNotes(resultNotes) {
  const notesMap = {};
  Object.entries(resultNotes || {}).forEach(([bnOriginal, obj]) => {
    const bn = safeExtractBarNumber(bnOriginal);
    const entry = {};
    if (obj.xrf) entry.xrf = obj.xrf;
    if (obj.cup) entry.cup = obj.cup;
    if (obj.dore) entry.dore = obj.dore;
    if (Object.keys(entry).length) notesMap[bn] = entry;
  });
  return notesMap;
}



/**
 * Builds payload for existing assay update - includes results arrays and bars
 */
function buildUpdateAssayPayload(state, companyId, useDistinctPins) {
  const xrfArray = buildXrfMetalResults(state.xrfResults)
  const cupArray = buildFireCupResults(state.fireResults)
  const doreArray = buildDoreResults(state.fireResults)
  const isPrimarySilver = state.metadata?.metalSymbol === METAL_SYMBOL.SILVER
  const sanitizedBars = sanitizeBarsForBackend(state.bars)
  const sanitizedPins = sanitizePinsForBackend(state.bars, state.pins, state.fireResults, isPrimarySilver)
  const barContract = wireBarContract(sanitizedBars, sanitizedPins, useDistinctPins)
  const notesMap = processResultNotes(state.resultNotes)

  return { 
    companyId,
    lotAssayId: state.lotAssayId,
    ...(Object.keys(notesMap || {}).length ? { assayResultNotes: notesMap } : {}),
    ...(xrfArray.length ? { xrfMetalResults: xrfArray } : {}),
    ...(cupArray.length ? { cupResults: cupArray } : {}),
    ...(doreArray.length ? { doreResults: doreArray } : {}),
    ...(sanitizedBars.length ? barContract : {}),
    ...(state.processedAt ? { processedAt: state.processedAt } : {}),
  }
}

/**
 * Builds complete payload for simplified REST endpoints
 * Combines all result types (XRF + Cup + Dore) + metadata for safety-first approach
 */
function buildCompletePayload(state, companyId, useDistinctPins) {
  const metalSymbol = state.metadata.metalSymbol || DEFAULT_METAL_SYMBOL
  const xrfArray = buildXrfMetalResults(state.xrfResults)
  const cupArray = buildFireCupResults(state.fireResults)
  const doreArray = buildDoreResults(state.fireResults)
  const isPrimarySilver = state.metadata?.metalSymbol === METAL_SYMBOL.SILVER
  const sanitizedBars = sanitizeBarsForBackend(state.bars)
  const sanitizedPins = sanitizePinsForBackend(state.bars, state.pins, state.fireResults, isPrimarySilver)
  const barContract = wireBarContract(sanitizedBars, sanitizedPins, useDistinctPins)
  const totalAssayWeight = calculateTotalAssayWeight(state.bars, state.pins)
  const notesMap = processResultNotes(state.resultNotes)
  
  // Build calcSnapshot from both calcOverrides and metadata
  const calcSnapshot = {
    ...state.calcOverrides,
    // Backend reads from marginOfErrorDefault, not marginOfError
    marginOfErrorDefault: state.metadata.marginOfError || DEFAULT_MARGIN_OF_ERROR,
  }

  return {
    companyId,
    refiningLotId: state.lotId,
    primaryMetal: metalSymbol,
    metalSymbol,
    receivedWeight: convertToRichWeight(totalAssayWeight, getContextUnit('lot')),
    processWeight: convertToRichWeight(totalAssayWeight, getContextUnit('lot')),
    marginOfError: state.metadata.marginOfError || DEFAULT_MARGIN_OF_ERROR,
    calcSnapshot,
    // notes only when non-empty
    ...(state.metadata.notes && String(state.metadata.notes).trim() ? { notes: state.metadata.notes } : {}),
    ...(Object.keys(notesMap || {}).length ? { assayResultNotes: notesMap } : {}),
    ...(xrfArray.length ? { xrfMetalResults: xrfArray } : {}),
    ...(cupArray.length ? { cupResults: cupArray } : {}),
    ...(doreArray.length ? { doreResults: doreArray } : {}),
    ...(sanitizedBars.length ? barContract : {}),
    ...(state.processedAt ? { processedAt: state.processedAt } : {}),
  }
}

/**
 * Returns bars that belong to this assay draft — either because they already
 * have results recorded against this assay, or because they originated from it.
 */
function barsForAssay(data) {
  if (!Array.isArray(data.bars)) return []
  const lotAssayId = data.lotAssay?.id
  const barIdsWithResults = new Set(
    (Array.isArray(data.assayResults) ? data.assayResults : [])
      .map(ar => ar.barId)
      .filter(Boolean)
  )
  return data.bars.filter(b =>
    b && (barIdsWithResults.has(b.id) || b.originatingLotAssayId === lotAssayId)
  )
}

/**
 * Processes lot assay metadata and returns update object
 */
function processLotAssayMetadata(lotAssay) {
  const metaUpdates = {}
  
  // map metalSymbol from backend (use metalSymbol if available, fallback to primaryMetal)
  const backendMetalSymbol = lotAssay.metalSymbol || lotAssay.primaryMetal
  if (backendMetalSymbol) {
    metaUpdates.metalSymbol = backendMetalSymbol
  }
  
  // map other fields
  if (lotAssay.notes) metaUpdates.notes = lotAssay.notes
  if (lotAssay.processedAt) metaUpdates.meltCompletedAt = lotAssay.processedAt
  
  if (lotAssay.calcSnapshot) {
    // Backend stores as marginOfErrorDefault in calcSnapshot
    if (lotAssay.calcSnapshot.marginOfErrorDefault !== undefined) {
      metaUpdates.marginOfError = lotAssay.calcSnapshot.marginOfErrorDefault
    }
    if (lotAssay.calcSnapshot.pgmHaircutFactor !== undefined) {
      metaUpdates.pgmHaircutFactor = lotAssay.calcSnapshot.pgmHaircutFactor
    }
    if (lotAssay.calcSnapshot.secondaryHaircutFactor !== undefined) {
      metaUpdates.secondaryHaircutFactor = lotAssay.calcSnapshot.secondaryHaircutFactor
    }

    // Hydrate UI metadata fields from calcSnapshot if present (persisted at save time)
    const snap = lotAssay.calcSnapshot
    const metaKeys = ['assayDueDate','advanceRequested','customerPinRequired','returnOrHoldPin','metalSymbol']
    metaKeys.forEach(k => {
      if (snap[k] !== undefined && snap[k] !== null && snap[k] !== '') {
        metaUpdates[k] = snap[k]
      }
    })
  }

  // Enforce: when a customer pin is required, Return/Hold Pin must be 'Yes' or 'No'
  if (metaUpdates.customerPinRequired === 'Yes' && metaUpdates.returnOrHoldPin === 'N/A') {
    metaUpdates.returnOrHoldPin = null
  }
  
  return metaUpdates
}

/**
 * Normalizes bars for UI display and creates bar ID mapping
 */
function normalizeBarsAndCreateMapping(barsForThisDraft, lotCode) {
  const barIdToNumber = {}
  
  const normalizedBars = barsForThisDraft.map((b, idx) => {
    // Use simple bar number for data keys and calculations
    const simpleBarNum = parseInt(b.barNumber || idx + 1)
    const serialNumber = lotCode ? `${lotCode}-${b.barNumber || idx + 1}` : String(simpleBarNum)
    if (b.id) barIdToNumber[b.id] = String(simpleBarNum)  // Use simple number for data lookup
    
    const baseBar = {
      serialNumber: serialNumber,  // Full identifier for display and notes
      barNumber: simpleBarNum,     // Simple numeric index for data keys
      meltCompletedAt: b.meltCompletedAt,
      furnaceId: b.furnaceId || null,
      meltTechnicianId: b.meltTechnicianId || b.technician || null,
      notes: b.notes || null,
      readOnly: true, // bars with existing results are read-only
    }
    
    // Strip legacy embedded pin fields, then normalize bar grossWeight via the shared door
    return normalizeBarWeights(stripEmbeddedPinFields({ ...baseBar, ...b }))
  })
  
  return { normalizedBars, barIdToNumber }
}

/**
 * Processes assay results and extracts XRF, fire, and notes data
 */
function processAssayResults(assayResults, barIdToNumber) {
  const xrfResults = {}
  const fireResults = {}
  const notesMap = {}

  assayResults.forEach(ar => {
    const barNum = String(barIdToNumber[ar.barId])
    if (!barNum || barNum === 'undefined') return

    // capture per-assay notes
    if (ar.notes) {
      if (!notesMap[barNum]) notesMap[barNum] = {};
      notesMap[barNum] = { ...notesMap[barNum], [ar.assayType]: ar.notes };
    }

    if (ar.assayType === 'xrf' && Array.isArray(ar.xrfMetalResults)) {
      if (!xrfResults[barNum]) xrfResults[barNum] = {}
      ar.xrfMetalResults.forEach(x => {
        const metal = (x.metalSymbol || '').toUpperCase()
        if (!metal) return
        const runIdx = (x.runNumber || 1) - 1
        if (!xrfResults[barNum][metal]) xrfResults[barNum][metal] = []
        const value = parseFloat(x.postMeltPercentage ?? x.postMeltPercentag ?? x.preMeltPercentage ?? 0)
        xrfResults[barNum][metal][runIdx] = value
      })
    }

    if (ar.assayType === 'cup' && Array.isArray(ar.cupResults)) {
      if (!fireResults[barNum]) fireResults[barNum] = {}
      ar.cupResults.forEach(c => {
        const idx = c.cupNumber
        const keyMap = {1: 'sample1', 2: 'sample2', 3: 'sample3', 0: 'control'}
        const sampleKey = keyMap[idx] || `sample${idx}`
        fireResults[barNum][sampleKey] = {
          gross: c.grossWeight,
          net: c.netWeight,
          outlier: c.outlier ?? false,
        }
      })
    }

    if (ar.assayType === 'dore' && Array.isArray(ar.doreResults)) {
      if (!fireResults[barNum]) fireResults[barNum] = {}
      ar.doreResults.forEach(d => {
        fireResults[barNum].control = {
          gross: d.grossWeight,
          net: d.netWeight,
          correctionFactor: parseFloat(d.correctionFactor || 0),
          useDoreCorrection: Boolean(d.useDoreCorrection),
        }
      })
    }
  })

  return { xrfResults, fireResults, notesMap }
}

const actions = {
  async fetchLotBars({commit,rootGetters,state}){
    const companyId = rootGetters['getSelectedCompany'].id
    if(!state.lotId) return []
    const data = await request.get(`/refinery/lots/${companyId}/${state.lotId}/bars`)
    const contract = parseBarContract(data, distinctPinsEnabled(rootGetters))
    commit('SET_ALL_BARS', contract.bars.map(bar =>
      normalizeBarWeights(stripEmbeddedPinFields(bar), { preserveOriginal: true })
    ))
    commit('SET_ALL_PINS', normalizeWeightArray(contract.pins, normalizePinWeights, { preserveOriginal: true }))
    return contract.bars
  },
  async createLotAssay({ state, commit, rootGetters }, receivedWeight) {
    if (!state.lotId) throw new Error('No lot ID available for assay creation')

    const companyId = rootGetters['getSelectedCompany'].id

    const payload = {
        companyId,
        refiningLotId: state.lotId,
        receivedWeight: convertToRichWeight(receivedWeight, getContextUnit('lot')),
        primaryMetal: (state.lotDetails?.refineryContract?.primaryMetalSymbol || state.lotDetails?.contract?.primaryMetalSymbol) || METAL_SYMBOL.GOLD,
        metalSymbol: state.metadata.metalSymbol || METAL_SYMBOL.GOLD,
        
    }

    const { result } = await request.post(`/refinery/lot-assays/${companyId}/${state.lotId}`, payload)

    if (result?.id) {
        commit('SET_LOT_ASSAY_ID', result.id)
        commit('SET_STARTING_GROSS_WEIGHT', receivedWeight)
    }

    return result
  },
  async updateLotAssayWeight({ state, commit, rootGetters }, receivedWeight) {
    if (!state.lotAssayId) throw new Error('No lot assay ID available for weight update')

    const companyId = rootGetters['getSelectedCompany'].id

    const payload = {
        companyId,
        lotAssayId: state.lotAssayId,
        receivedWeight: convertToRichWeight(receivedWeight, getContextUnit('lot'))
    }

    const { result } = await request.patch(`/refinery/lot-assays/${companyId}/${state.lotAssayId}`, payload)

    if (result?.id) {
        commit('SET_STARTING_GROSS_WEIGHT', receivedWeight)
    }

    return result
  },
  // initialise draft – either new or fetch existing
  initDraft({ commit }, { lotId, existingDraft = null }) {
    commit('RESET_STATE')
    commit('SET_LOT_ID', lotId)
    if (existingDraft) {
      // hydrate all keys
      Object.keys(existingDraft).forEach((k) => {
        if (k === 'metadata') {
          Object.assign(state.metadata, existingDraft.metadata)
        } else commit('SET_' + k.toUpperCase(), existingDraft[k])
      })
    } else {
      // start with empty manifest for new assay - users can import bars from "other bars"
      commit('SET_BARS', [])
    }
  },
  updateMetaField({ commit }, payload) {
    commit('SET_METADATA_FIELD', payload)
  },
  async saveDraft({ state, commit, rootGetters, dispatch }) {
    if (!state.lotId) throw new Error(ERROR_MESSAGES.NO_LOT_ID)
    
    const companyId = rootGetters['getSelectedCompany'].id
    
    if (state.lotAssayId) {
      // Update existing lot assay - send results arrays + calc settings separately
      const updatePayload = buildUpdateAssayPayload(state, companyId, distinctPinsEnabled(rootGetters))
      await request.put(`/refinery/lot-assays/${companyId}/${state.lotAssayId}/results`, updatePayload)
      
      // Save calc settings (including marginOfError) via separate endpoint
      await dispatch('saveCalcOverrides')
      
      // Persist key metadata to the calcSnapshot as well for reliable hydration in finalized view
      const snapshotMeta = {
        assayDueDate: state.metadata.assayDueDate,
        advanceRequested: state.metadata.advanceRequested,
        customerPinRequired: state.metadata.customerPinRequired,
        returnOrHoldPin: state.metadata.returnOrHoldPin,
        metalSymbol: state.metadata.metalSymbol,
      }
      await request.patch(`/refinery/lot-assays/${companyId}/${state.lotAssayId}`,{ calcSnapshot: { ...state.calcOverrides, ...snapshotMeta } }).catch(()=>{})
      
      return { isNewDraft: false, lotAssayId: state.lotAssayId }
    } else {
      // Create new lot assay with all metadata and results
      const createPayload = buildCompletePayload(state, companyId, distinctPinsEnabled(rootGetters))
      const { result } = await request.post(`/refinery/lots/${companyId}/${state.lotId}/assays`, createPayload)
      
      let isNewDraft = false
      if (result) {
        if (result.lotAssayId) {
          commit('SET_LOT_ASSAY_ID', result.lotAssayId)
          isNewDraft = true
        }
        if (Array.isArray(result.assayResultIds)) {
          commit('SET_ASSAY_RESULT_IDS', result.assayResultIds)
        }
      }

      // Persist key metadata into calcSnapshot immediately after creation for reliable hydration
      try {
        if (state.lotAssayId) {
          const snapshotMeta = {
            assayDueDate: state.metadata.assayDueDate,
            advanceRequested: state.metadata.advanceRequested,
            customerPinRequired: state.metadata.customerPinRequired,
            returnOrHoldPin: state.metadata.returnOrHoldPin,
            metalSymbol: state.metadata.metalSymbol,
          }
          await request.patch(`/refinery/lot-assays/${companyId}/${state.lotAssayId}`, { calcSnapshot: { ...state.calcOverrides, ...snapshotMeta } }).catch(() => {})
        }
      } catch (e) { /* no-op: snapshot persistence is best-effort */ }
      
      return { isNewDraft, lotAssayId: state.lotAssayId }
    }
  },
  async deleteAnalysis({ commit, state, rootGetters }) {
    if (!state.lotAssayId) throw new Error('lotAssayId required')
    const companyId = rootGetters['getSelectedCompany'].id
    await request.delete(`/refinery/assay-results/${companyId}/${state.lotAssayId}`)
    commit('SET_ASSAY_RESULT_IDS', [])
    commit('SET_LOT_ASSAY_ID', null)
    commit('RESET_RESULTS')
  },
  async finalizeDraft({ state, rootGetters }, { mode } = {}) { // eslint-disable-line no-unused-vars
    // if no draft exists yet, create it first, then finalize
    if (!state.lotAssayId) {
      await this.dispatch('assayDraft/saveDraft')
      if (!state.lotAssayId) {
        throw new Error(ERROR_MESSAGES.DRAFT_CREATION_FAILED)
      }
    } else {
      // Save current draft state before finalizing to ensure current data is persisted
      await this.dispatch('assayDraft/saveDraft')
    }
    const companyId = rootGetters['getSelectedCompany'].id
    const payload = {
      lotAssayId: state.lotAssayId,
      completedAt: new Date().toISOString(),
      calcSnapshotOverride: state.calcOverrides,
    }
    await request.post(`/refinery/assay-results/${companyId}/${state.lotAssayId}/finalize`, payload)
    // reflect finalized state in UI immediately
    this.commit('assayDraft/SET_STATUS', 'submitted')
  },
  async saveCalcOverrides({state, rootGetters, commit}) {
    if(!state.lotAssayId) return;
    const companyId = rootGetters['getSelectedCompany'].id;
    await request.patch(`/refinery/lot-assays/${companyId}/${state.lotAssayId}`, { calcSnapshot: state.calcOverrides });
    
    // Sync metadata from calcOverrides after save so components stay in sync
    if (state.calcOverrides.pgmHaircutFactor !== undefined) {
      commit('SET_METADATA_FIELD', { key: 'pgmHaircutFactor', value: state.calcOverrides.pgmHaircutFactor });
    }
    if (state.calcOverrides.secondaryHaircutFactor !== undefined) {
      commit('SET_METADATA_FIELD', { key: 'secondaryHaircutFactor', value: state.calcOverrides.secondaryHaircutFactor });
    }
    if (state.calcOverrides.marginOfErrorDefault !== undefined) {
      commit('SET_METADATA_FIELD', { key: 'marginOfError', value: state.calcOverrides.marginOfErrorDefault });
    }
  },
  async updateXrfWeighting({ commit, dispatch }, { useWeightedXrfAveraging }) {
    commit('SET_CALC_OVERRIDE', { key: 'useWeightedXrfAveraging', value: useWeightedXrfAveraging })
    await dispatch('saveCalcOverrides')
  },
  async uploadPhoto({ commit, rootGetters }, { lotAssayId, file, caption, customerVisible, sortOrder }) {
    const companyId = rootGetters['getSelectedCompany'].id
    const { photo: registeredPhoto, putUrl } = await registerAssayPhoto({
      companyId,
      lotAssayId,
      contentType: file.type,
      caption,
      customerVisible,
      sortOrder,
    })

    try {
      await uploadAssayPhotoToS3({
        putUrl,
        file,
        contentType: file.type,
      })
    } catch (putError) {
      try {
        await deleteAssayPhoto({ companyId, lotAssayId, photoId: registeredPhoto.id })
      } catch (_cleanupError) {
        // swallow — do not mask the original PUT error
      }
      throw putError
    }

    const { photo: confirmedPhoto } = await confirmAssayPhoto({
      companyId,
      lotAssayId,
      photoId: registeredPhoto.id,
    })

    commit('ADD_PHOTO_TO_ASSAY', { lotAssayId, photo: confirmedPhoto })
    return confirmedPhoto
  },
  async removePhoto({ commit, rootGetters }, { lotAssayId, photoId }) {
    const companyId = rootGetters['getSelectedCompany'].id
    const { photo: deletedPhoto } = await deleteAssayPhoto({ companyId, lotAssayId, photoId })
    commit('REMOVE_PHOTO_FROM_ASSAY', { lotAssayId, photoId })
    return deletedPhoto
  },
  async setPhotoVisibility({ commit, rootGetters }, { lotAssayId, photoId, customerVisible }) {
    const companyId = rootGetters['getSelectedCompany'].id
    const { photo: updatedPhoto } = await setAssayPhotoVisibility({
      companyId,
      lotAssayId,
      photoId,
      customerVisible,
    })
    commit('UPDATE_PHOTO_IN_ASSAY', { lotAssayId, photo: updatedPhoto })
    return updatedPhoto
  },
  async updateFireWeighting({ commit, dispatch }, { useWeightedFireAveraging }) {
    commit('SET_CALC_OVERRIDE', { key: 'useWeightedFireAveraging', value: useWeightedFireAveraging })
    await dispatch('saveCalcOverrides')
  },
  async updateXrfLock({ commit, dispatch }, { locked }) {
    commit('SET_CALC_OVERRIDE', { key: 'xrfLocked', value: Boolean(locked) })
    await dispatch('saveCalcOverrides')
  },
  async updateFireLock({ commit, dispatch }, { locked }) {
    commit('SET_CALC_OVERRIDE', { key: 'fireLocked', value: Boolean(locked) })
    await dispatch('saveCalcOverrides')
  },
  setAllBarsUseDoreCorrection({ state, commit }, useDoreCorrection) {
    // Update useDoreCorrection for all bars with doré results
    const updatedFireResults = { ...state.fireResults }
    
    Object.keys(updatedFireResults).forEach(barNumber => {
      const barResults = updatedFireResults[barNumber]
      if (barResults && barResults.control) {
        updatedFireResults[barNumber] = {
          ...barResults,
          control: {
            ...barResults.control,
            useDoreCorrection: Boolean(useDoreCorrection)
          }
        }
      }
    })
    
    commit('SET_FIRE_RESULTS', updatedFireResults)
  },
  async finalize({ state, rootGetters }) {
    if (!state.lotId) throw new Error('lotId required')
    const companyId = rootGetters['getSelectedCompany'].id
    const bars = sanitizeBarsForBackend(state.bars)
    const pins = sanitizePinsForBackend(
      state.bars,
      state.pins,
      state.fireResults,
      state.metadata?.metalSymbol === METAL_SYMBOL.SILVER
    )
    const payload = {
      ...state,
      ...wireBarContract(bars, pins, distinctPinsEnabled(rootGetters))
    }
    await request.post(`/refinery/lots/${companyId}/${state.lotId}/assay-finalize`, payload)
  },
  async fetchLotDetails({ commit, rootGetters }, lotId) {
    const companyId = rootGetters['getSelectedCompany'].id
    try {
      const data = await request.get(`/refinery/lots/${companyId}/${lotId}`)
      if (!data) return

      // Store the full lot details for component access
      commit('SET_LOT_DETAILS', data)
      
      if (data.lotCode !== undefined) commit('SET_LOT_CODE', data.lotCode)

      // derive meta fields with robust contract fallbacks
      const derived = {}
      // Advance requested: prefer lot flag, fallback to contract default if available
      if (typeof data.advanceEnabled === 'boolean') {
        derived.advanceRequested = data.advanceEnabled ? 'Yes' : 'No'
      } else if (data.refineryContract && typeof data.refineryContract.advanceEnabled === 'boolean') {
        derived.advanceRequested = data.refineryContract.advanceEnabled ? 'Yes' : 'No'
      }

      // Customer pin required: derive from contract's PIN fee status
      // If contract has customerPinFee enabled, default to 'Yes', otherwise 'No'
      const contractTerms = data?.refineryContract?.terms || []
      const pinFeeTerm = Array.isArray(contractTerms) 
        ? contractTerms.find(term => term.type === 'customerPinFee')
        : null
      derived.customerPinRequired = (pinFeeTerm && pinFeeTerm.enabled === true) ? 'Yes' : 'No'

      // Do not set a default for Return/Hold Pin; require explicit user choice when pin is required

      // metal symbol: prefer first existing assay; fallback to contract primary metal
      if (Array.isArray(data.assays) && data.assays.length) {
        const assayMetalSymbol = data.assays[0].metalSymbol || data.assays[0].primaryMetal
        if (assayMetalSymbol) {
          derived.metalSymbol = assayMetalSymbol
        }
      }
      if (!derived.metalSymbol) {
        const contractPrimary = data?.refineryContract?.primaryMetalSymbol || data?.contract?.primaryMetalSymbol
        if (contractPrimary) derived.metalSymbol = contractPrimary
      }

      // derive assay due date – receivedDate + DEFAULT_ASSAY_TAT_DAYS
      if (!state.metadata.assayDueDate) {
        const baseDateStr = data.receivedDate
        if (baseDateStr) {
          const base = new Date(baseDateStr)
          if (!isNaN(base)) {
            base.setDate(base.getDate() + DEFAULT_ASSAY_TAT_DAYS)
            const iso = base.toISOString().slice(0,10) // yyyy-mm-dd
            commit('SET_METADATA_FIELD', { key: 'assayDueDate', value: iso })
          }
        }
      }

      // load calcSnapshot from first lotAssay if present
      if(Array.isArray(data.assays) && data.assays.length){
        const snap = data.assays[0].calcSnapshot || {};
        Object.entries(snap).forEach(([k,v])=>commit('SET_CALC_OVERRIDE',{key:k,value:v}))
      }

      // set status finalized if completedAt present
      if(Array.isArray(data.assays) && data.assays.length){
        const comp = (data.assays.find(a => a && a.completedAt) || {}).completedAt;
        if(comp){ commit('SET_STATUS', 'submitted') }
      }

      // commit each field
      Object.entries(derived).forEach(([k,v])=>commit('SET_METADATA_FIELD',{key:k,value:v}))

    } catch(e) {
      console.warn('failed to fetch lot details', e)
    }
  },
  async hydrateDraft({ commit, state, rootGetters }, { companyId, lotAssayId }) {
    try {
      const data = await request.get(`/refinery/assay-results/${companyId}/${lotAssayId}`)
      if (!data) return

      // set core ids
      commit('SET_LOT_ASSAY_ID', lotAssayId)
      if (data.assayResults?.length) {
        const assayResultIds = data.assayResults.map(ar => ar.id)
        commit('SET_ASSAY_RESULT_IDS', assayResultIds)
      }

      const contract = parseBarContract(data, distinctPinsEnabled(rootGetters))
      const canonicalData = { ...data, bars: contract.bars }
      const barsForThisDraft = barsForAssay(canonicalData)
      const pinsForThisDraft = contract.pins.filter(pin =>
        barsForThisDraft.some(bar => pinBelongsToBar(pin, bar))
      )
      // Always commit: an empty server manifest must clear stale local pins
      commit('SET_PINS', normalizeWeightArray(pinsForThisDraft, normalizePinWeights, { preserveOriginal: true }))

      // hydrate metadata from lotAssay
      if (data.lotAssay) {
        const metaUpdates = processLotAssayMetadata(data.lotAssay)
        
        // set status based on completion
        if (data.lotAssay.completedAt) {
          commit('SET_STATUS', 'submitted')
        }

        if (data.lotAssay.receivedWeight) {
          commit('SET_STARTING_GROSS_WEIGHT', data.lotAssay.receivedWeight)
        }

        // preserve processedAt timestamp
        if (data.lotAssay.processedAt) {
          state.processedAt = data.lotAssay.processedAt
        }
        
        // commit metadata updates
        Object.entries(metaUpdates).forEach(([key, value]) => {
          commit('SET_METADATA_FIELD', { key, value })
        })

      // Backfill meta fields from refining lot/contract when missing
        const refiningLot = data.lotAssay.refiningLot || {}

        // Advance Requested: from lot, then contract
        if (typeof refiningLot.advanceEnabled === 'boolean') {
          commit('SET_METADATA_FIELD', { key: 'advanceRequested', value: refiningLot.advanceEnabled ? 'Yes' : 'No' })
        } else if (refiningLot.refineryContract && typeof refiningLot.refineryContract.advanceEnabled === 'boolean') {
          commit('SET_METADATA_FIELD', { key: 'advanceRequested', value: refiningLot.refineryContract.advanceEnabled ? 'Yes' : 'No' })
        }

        // Customer pin required: derive from contract's PIN fee status
        // Check both hydrateDraft response and already-loaded lotDetails (from fetchLotDetails)
        const contractTerms = refiningLot?.refineryContract?.terms 
          || state.lotDetails?.refineryContract?.terms 
          || []
        const pinFeeTerm = Array.isArray(contractTerms) 
          ? contractTerms.find(term => term.type === 'customerPinFee')
          : null
        const customerPinRequired = (pinFeeTerm && pinFeeTerm.enabled === true) ? 'Yes' : 'No'
        commit('SET_METADATA_FIELD', { key: 'customerPinRequired', value: customerPinRequired })

        // Do not default Return/Hold Pin when pin is required; enforce explicit selection in UI

        // Assay Due Date: derive from receivedDate + DEFAULT_ASSAY_TAT_DAYS when missing
        if (!state.metadata.assayDueDate) {
          const baseDateStr = refiningLot.receivedDate
          if (baseDateStr) {
            const base = new Date(baseDateStr)
            if (!isNaN(base)) {
              base.setDate(base.getDate() + DEFAULT_ASSAY_TAT_DAYS)
              const iso = base.toISOString().slice(0,10)
              commit('SET_METADATA_FIELD', { key: 'assayDueDate', value: iso })
            }
          }
        }
      }

      // normalize & commit bars if present
      let lotCode = data.lotAssay?.refiningLot?.lotCode || data.lotAssay?.refiningLot?.lot_code || ''
      let barIdToNumber = {}
      if (Array.isArray(barsForThisDraft) && barsForThisDraft.length) {
        const { normalizedBars, barIdToNumber: mapping } = normalizeBarsAndCreateMapping(barsForThisDraft, lotCode)
        barIdToNumber = mapping

        // Preserve local bar changes if they exist, otherwise use server data
        const currentBars = state.bars || []
        const hasLocalChanges = currentBars.length > 0 &&
          JSON.stringify(currentBars) !== JSON.stringify(normalizedBars)

        if (hasLocalChanges) {
          // Keep local changes - don't overwrite with server data
          // Just update the bar number mapping for results processing
          const localBarIdToNumber = {}
          currentBars.forEach(bar => {
            if (bar.id) localBarIdToNumber[bar.id] = bar.barNumber
          })
          barIdToNumber = { ...barIdToNumber, ...localBarIdToNumber }
        } else {
          // No local changes, safe to use server data
          commit('SET_BARS', normalizedBars)
        }

        commit('SET_MANIFEST_CONFIRMED', true)
      }

      // hydrate xrf + fire only when assayResults present
      if (Array.isArray(data.assayResults) && data.assayResults.length) {
        const { xrfResults, fireResults, notesMap } = processAssayResults(data.assayResults, barIdToNumber)

        if (Object.keys(xrfResults).length) {
          commit('SET_XRF_RESULTS', xrfResults)
        }
        if (Object.keys(fireResults).length) commit('SET_FIRE_RESULTS', fireResults)

        // persist notesMap into state
        Object.entries(notesMap).forEach(([bn,obj])=>{
          Object.entries(obj).forEach(([atype,note])=>{
            commit('SET_RESULT_NOTE',{ barNumber: bn, assayType: atype, note });
          });
        });
      }

    } catch (e) {
      console.warn('failed to hydrate draft', e)
      throw e
    }
  },
}

/**
 * Extract numeric bar number from composite bar identifier
 * @param {string|number} barNumber - Bar identifier (e.g., "RL37-1" or "1")
 * @returns {number|null} - Numeric bar number or null if invalid
 * @example extractNumericBarNumber("RL37-1") // returns 1
 */
function extractNumericBarNumber(barNumber) {
  const parts = String(barNumber).split('-')
  return parseInt(parts.pop()) || null
}

// helper: convert fireResults {barNumber:{sample:{}}} -> cupResults array with barNumber  
function buildFireCupResults(fireResults) {
  const cupResults = []
  
  Object.entries(fireResults || {}).forEach(([barNumber, barData]) => {
    if (!barData) return
    
    const numericBarNumber = extractNumericBarNumber(barNumber)
    if (!numericBarNumber) return
    
    // map sample keys to cup numbers
    const sampleToCupMap = {
      'sample1': 1,
      'sample2': 2, 
      'sample3': 3,
      // 'control': 0  // TODO: handle control samples separately
    }
    
    Object.entries(barData).forEach(([sampleKey, sampleData]) => {
      if (!sampleData || typeof sampleData !== 'object') return
      
      const cupNumber = sampleToCupMap[sampleKey]
      if (cupNumber === undefined) return
      
      // extract weights in rich weight format
      const grossWeight = sampleData.gross
      const netWeight = sampleData.net
      
      if (!grossWeight || !netWeight) return
      
      // Clean weight objects for API - remove _displayValue which is frontend-only
      const cleanWeight = (weightObj) => {
        if (typeof weightObj === 'object' && weightObj.grams !== undefined) {
          return { grams: weightObj.grams }
        }
        return typeof weightObj === 'object' ? weightObj : { grams: parseFloat(weightObj) }
      }
      
      cupResults.push({
        cupNumber,
        barNumber: numericBarNumber,
        grossWeight: cleanWeight(grossWeight),
        netWeight: cleanWeight(netWeight),
        outlier: sampleData.outlier ?? false
      })
    })
  })
  
  return cupResults
}

// helper: convert xrfResults {barNumber:{metal:[vals]}} -> xrfMetalResults array with barNumber
function buildXrfMetalResults(xrfResults){
  const runs = [];

  Object.entries(xrfResults || {}).forEach(([barNumber, barData]) => {
    if (!barData) return;
    
    const numericBarNumber = extractNumericBarNumber(barNumber)
    if (!numericBarNumber) return;

    Object.entries(barData).forEach(([metal, values]) => {
      if (!Array.isArray(values)) return;
      
      values.forEach((pct, runIdx) => {
        if (typeof pct === 'number' && !isNaN(pct)) {
          runs.push({
            runNumber: runIdx + 1,
            metalSymbol: metal.toUpperCase(),
            postMeltPercentage: pct,
            barNumber: numericBarNumber,
          });
        }
      });
    });
  });

  return runs;
}

// helper: extract control/dore samples from fireResults -> doreResults array
function buildDoreResults(fireResults) {
  const doreResults = []
  
  Object.entries(fireResults || {}).forEach(([barNumber, barData]) => {
    if (!barData || !barData.control) return
    
    const numericBarNumber = extractNumericBarNumber(barNumber)
    if (!numericBarNumber) return
    
    const controlData = barData.control
    const grossWeight = controlData.gross
    const netWeight = controlData.net
    
    if (!grossWeight || !netWeight) return
    
    // clean weight objects (remove _displayValue)
    const cleanWeight = (weightObj) => {
      if (typeof weightObj === "object" && weightObj.grams !== undefined) {
        return { grams: weightObj.grams }
      }
      return typeof weightObj === "object" ? weightObj : { grams: parseFloat(weightObj) }
    }
    
    doreResults.push({
      barNumber: numericBarNumber,
      grossWeight: cleanWeight(grossWeight),
      netWeight: cleanWeight(netWeight),
      correctionFactor: parseFloat(controlData.correctionFactor) || 0,
      useDoreCorrection: Boolean(controlData.useDoreCorrection)
    })
  })
  
  return doreResults
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
} 